import React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import usePermissions, { Features } from '../../../permissions/usePermissions';
import ComponentNames from '../../../permissions/constants/componentNames';
import { useStore } from '../../../store/mst';
import OrderDetailsPanel from '../../orders/OrderDetails/OrderDetailsPanel/index';
import RouteDetailsPanel from './RouteDetailsPanel';
import { TRANSITION_DELAY_MILLI } from '../../../constants';
import useDelayUnmount from '../../../hooks/useDelayUnmount';
import { PanelType } from './PanelType';
import { getCustomerOrderReturn } from '../../customerProfiles/customerOrderNavigation';
import { SchedulingQueryParams } from '../../../store/mst/Router/constants';

interface Props {
  panelOpen: boolean;
}

// Stack both panels in the same space so RouteDetailsPanel can stay mounted when
// we switch to order details; navigating back then shows already-rendered content.
const PanelStack = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const PanelLayer = styled.div<{ $zIndex: number }>`
  position: absolute;
  inset: 0;
  z-index: ${({ $zIndex }) => $zIndex};
`;

// The panels without any docking chrome, so they can fill either the right-hand
// Drawer (`DetailsPanel`) or the Page's full-screen overlay on narrow viewports.
const DetailsPanelContent: React.FC<Props> = ({ panelOpen }) => {
  const store = useStore();
  const destination = getCustomerOrderReturn(store.router?.location?.state);
  const returnToCustomer = destination ? () => store.router.replace(destination) : undefined;
  const {
    selectedVisit, selectedRoute, selectedRouteEvent, currentDetailsView,
  } = store.ui;
  const orderUuid = store.router?.queryParams?.get(SchedulingQueryParams.ORDER) ?? undefined;
  const orderSelected = !!(selectedVisit || orderUuid);

  const hasTransitionedInRouteDetails = (!!selectedRoute || !!selectedRouteEvent) && !orderSelected;
  const hasTransitionedOrderDetailsPanel = useDelayUnmount(orderSelected, TRANSITION_DELAY_MILLI);

  const {
    canViewVisits, canViewRoutes,
  } = usePermissions(Features.Routes, ComponentNames.DetailsPanel);

  const showRouteDetailsPanel = canViewRoutes && (!!selectedRoute || !!selectedRouteEvent);
  const showOrderDetailsPanel = orderSelected && hasTransitionedOrderDetailsPanel && canViewVisits;

  return (
    <PanelStack>
      {showRouteDetailsPanel && (
        <PanelLayer $zIndex={0}>
          <RouteDetailsPanel
            isNextViewOrders={orderSelected}
            visible={!orderSelected && currentDetailsView === PanelType.ROUTE_DETAILS}
            hasTransitionedIn={hasTransitionedInRouteDetails}
          />
        </PanelLayer>
      )}
      {showOrderDetailsPanel && (
        <PanelLayer $zIndex={1}>
          <OrderDetailsPanel
            visit={selectedVisit}
            orderUuid={orderUuid}
            visible={panelOpen}
            hasTransitionedIn={hasTransitionedOrderDetailsPanel}
            isNextViewRoutes={currentDetailsView === PanelType.ROUTE_DETAILS}
            onBack={returnToCustomer}
          />
        </PanelLayer>
      )}
    </PanelStack>
  );
};

export default observer(DetailsPanelContent);
